import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFetcher } from '@/provider/useFetcher'
import { apiFetch } from './api'
import type {
  CreateScheduleInput,
  PromptEditEntry,
  SubmitRunInput,
} from './queries.types'
import { submitRun } from './runs'
import type { Run, RunStatus, Schedule, SubmitRunResult } from './types'

export type { CreateScheduleInput, PromptEditEntry, SubmitRunInput }

// Single source of truth for the schedules list cache key so the list query and
// the create/delete mutations that invalidate it can't drift apart.
const SCHEDULES_KEY = ['schedules'] as const

// Stop polling after this many successful fetches of a still-non-terminal run
// (~POLL_MS * MAX_POLLS ≈ 10 min). A run that never reaches a terminal status
// (dead worker, dropped job) would otherwise poll forever while the tab is
// open. The user can remount/refetch to resume.
const POLL_MS = 2000
const MAX_POLLS = 300

// Pure polling cadence: stop on terminal status or once the poll budget is
// exhausted, otherwise poll every POLL_MS. Exported for direct unit testing.
export function runRefetchInterval(query: {
  state: { data?: { status?: RunStatus } | null; dataUpdateCount: number }
}): number | false {
  const s = query.state.data?.status
  if (s === 'complete' || s === 'error') return false
  if (query.state.dataUpdateCount >= MAX_POLLS) return false

  return POLL_MS
}

export function useRun(runId: string) {
  const fetcher = useFetcher()

  return useQuery({
    queryKey: ['run', runId],
    queryFn: ({ signal }) =>
      apiFetch<Run>(fetcher, `/api/run/${runId}`, { signal }),
    // Don't fire `/api/run/` (no id) when runId is missing/empty.
    enabled: !!runId,
    refetchInterval: (query) => runRefetchInterval(query),
    // A backgrounded tab shouldn't keep hammering the run endpoint.
    refetchIntervalInBackground: false,
  })
}

interface UseSubmitRunOptions {
  forceFresh?: boolean
}

export function useSubmitRun(options: UseSubmitRunOptions = {}) {
  const fetcher = useFetcher()

  return useMutation({
    mutationFn: async (input: SubmitRunInput) => {
      const forceFresh = options.forceFresh ?? input.forceFresh ?? false
      const result = await submitRun(fetcher, {
        ...input,
        forceFresh: forceFresh || undefined,
      })

      if (forceFresh && result.forceFreshAccepted !== true) {
        throw new Error(
          'Fresh reruns are temporarily unavailable. Try again shortly.'
        )
      }

      return result
    },
  })
}

// Lists the caller's schedules (owner-scoped on the backend). Reachable
// only when auth is present (Schedules is routed under the authed branch). The
// list endpoint wraps rows in a `{ schedules: [...] }` envelope, so the query
// unwraps it to the bare array the UI maps over.
interface SchedulesEnvelope {
  schedules: Schedule[]
}

export function useSchedules() {
  const fetcher = useFetcher()

  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: async ({ signal }) => {
      const res = await apiFetch<SchedulesEnvelope>(fetcher, '/api/schedules', {
        signal,
      })

      return res?.schedules ?? []
    },
  })
}

// POST /api/schedules — backend defaults mode to "direct" and market to
// "Global", so only brand + category are strictly required. On success we
// invalidate the list so the new row appears without a manual refetch.
export function useCreateSchedule() {
  const fetcher = useFetcher()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: CreateScheduleInput) =>
      apiFetch<{ status: string }>(fetcher, '/api/schedules', {
        method: 'POST',
        body: { mode: 'direct', ...input },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  })
}

// DELETE /api/schedules/{id} — owner-scoped on the backend.
export function useDeleteSchedule() {
  const fetcher = useFetcher()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ status: string }>(fetcher, `/api/schedules/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  })
}

export function useEditPrompts(runId: string) {
  const fetcher = useFetcher()

  return useMutation({
    mutationFn: async (edits: PromptEditEntry[]) => {
      const result = await apiFetch<SubmitRunResult>(
        fetcher,
        `/api/run/${runId}/prompts`,
        {
          method: 'POST',
          body: { edits },
        }
      )

      if (!result) throw new Error('Prompt edit returned an empty response')

      return result
    },
  })
}
